import React, { memo, useContext } from 'react';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component.ts';

import { useRegisterStorageEnforcementBanner } from './useRegisterStorageEnforcementBanner.ts';
import { HardBanner } from './StorageEnforcementBannerComponent/HardBanner.tsx';
import { SoftBanner } from './StorageEnforcementBannerComponent/SoftBanner.tsx';

export interface StorageEnforcementBannerProps {
	bannerState: BannerStateContainer;
}

const StorageEnforcementBannerContainer = memo(({ bannerState }: StorageEnforcementBannerProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { shouldRender, shouldRenderHard, error, loading, onDismiss } =
		useRegisterStorageEnforcementBanner(bannerState);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (error) {
		experienceTracker.stopOnError({
			name: ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE,
			error,
			attributes: {
				shouldRender,
				shouldRenderHard,
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				isSSR: process.env.REACT_SSR, // Want to be able to track server side errors vs SPA.
			},
		});

		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
			return null;
		}

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'errored',
				actionSubject: 'storageEnforcementBanner',
				source: 'onboarding',
				attributes: {
					message: error.message,
					stack: error.stack,
				},
			},
		}).fire();

		return <ErrorDisplay error={error} />;
	}

	if (!shouldRender) {
		if (!loading) {
			/**
			 * No render of the banner an abort to differentiate it from an actual render.
			 * this scenario it means that none of the queries errored out.
			 */
			experienceTracker.abort({
				name: ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE,
				reason: 'under storage enforcement threshold',
			});
		}

		return null;
	}

	return shouldRenderHard ? <HardBanner /> : <SoftBanner onDismiss={onDismiss} />;
});

export const StorageEnforcementBanner = () => (
	<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
		<ExperienceStart name={ONBOARDING_STORAGE_ENFORCEMENT_BANNER_EXPERIENCE} />
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => (
				<StorageEnforcementBannerContainer bannerState={bannerState} />
			)}
		</Subscribe>
	</ErrorBoundary>
);
